import { Flex, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import { useLEAccordionItemContext } from '../../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import Button from '../../../../../components/ui/buttons/Button';
import useListingSubmit from '../../../../../hooks/useListingSubmit';
import { useMarketplaceExternalId } from '../../../../../services/marketplace/externalId';
import MarketplaceListingConsentCopy, { LIST_BUTTON_COPY } from './MarketplaceListingConsentCopy';
import useMarketplaceListingConsent from './useMarketplaceListingConsent';

type PrelistSubmitFooterProps = {
  dealId: string;
  isLast: boolean;
  isComplete: boolean;
  isBusy?: boolean;
  helperText?: string;
};

const PrelistSubmitFooter = ({
  dealId,
  isLast,
  isComplete,
  isBusy = false,
  helperText,
}: PrelistSubmitFooterProps) => {
  const { updateState, setOpenAccordionIndex, index } = useLEAccordionItemContext();
  const externalId = useMarketplaceExternalId(dealId);
  const { submit: activateNoPrice, isSubmitting: isActivating } = useListingSubmit({
    dealId,
    externalId,
    onListed: () => undefined,
    showErrorToast: true,
  });
  const { consentText, saveConsent } = useMarketplaceListingConsent(dealId);
  const [isSavingConsent, setIsSavingConsent] = useState(false);

  const handleSubmit = async () => {
    if (isLast) {
      try {
        setIsSavingConsent(true);
        await saveConsent();
        const listed = await activateNoPrice();
        updateState({ status: listed ? 'complete' : 'error' });
      } catch {
        updateState({ status: 'error' });
      } finally {
        setIsSavingConsent(false);
      }
    } else {
      updateState({ status: 'complete' });
      setOpenAccordionIndex(index + 1);
    }
  };

  const loading = isActivating || isSavingConsent;

  return (
    <>
      {isLast && <MarketplaceListingConsentCopy consentText={consentText} />}

      <VStack spacing={2} pt={2}>
        <Flex justify="center">
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={isActivating ? 'Listing...' : 'Saving...'}
            isDisabled={!isComplete || isBusy || (isLast && !consentText)}
            px={10}
          >
            {isLast ? LIST_BUTTON_COPY : 'NEXT'}
          </Button>
        </Flex>
        {helperText && (
          <Text fontSize="sm" color="grayLight" textAlign="center">
            {helperText}
          </Text>
        )}
      </VStack>
    </>
  );
};

export default PrelistSubmitFooter;
